import { Schema } from "effect";

const MeshHttpStatus = /* @__PURE__ */ (() => Schema.Int.check(Schema.isBetween({ minimum: 100, maximum: 599 })))();
const MeshErrorFields = /* @__PURE__ */ (() => Schema.Struct({ code: Schema.String, message: Schema.String, status: MeshHttpStatus }))();
type MeshErrorProperties = typeof MeshErrorFields["~type.make.in"];

export const MeshContainerRemoteError = /* @__PURE__ */ (() => {
  class MeshContainerRemoteError extends Schema.TaggedError<MeshContainerRemoteError>()("MeshContainerRemoteError", MeshErrorFields) {
    readonly #constructed = true;
    static readonly isConstructed = Schema.is(Schema.declare((value): value is MeshContainerRemoteError =>
      typeof value === "object" && value !== null && #constructed in value));

    static [Symbol.hasInstance](value: unknown): value is MeshRemoteFailure {
      return this === MeshContainerRemoteError ? isRemoteFailureInstance(value) : Function.prototype[Symbol.hasInstance].call(this, value);
    }

    constructor(code: string, message: string, status?: number);
    constructor(properties: MeshErrorProperties, options?: Schema.MakeOptions);
    constructor(code: string | MeshErrorProperties, message?: string | Schema.MakeOptions, status = 502) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "", status } : code,
        typeof message === "object" ? message : undefined);
      this.name = "MeshContainerRemoteError";
    }

    toJSON() { return { name: this.name, code: this.code, status: this.status }; }
  }
  return MeshContainerRemoteError;
})();
export type MeshContainerRemoteError = InstanceType<typeof MeshContainerRemoteError>;

/** Configuration refusal belongs to the mesh error model, including its status. */
export const meshConfigurationError = (message: string) => new MeshContainerRemoteError({
  code: "MESH_CONFIG_INVALID", message, status: 500
});

export const MeshProtocolError = /* @__PURE__ */ (() => {
  class MeshProtocolError extends Schema.TaggedError<MeshProtocolError>()("MeshProtocolError", MeshErrorFields) {
    readonly #constructed = true;
    static readonly isConstructed = Schema.is(Schema.declare((value): value is MeshProtocolError =>
      typeof value === "object" && value !== null && #constructed in value));

    static [Symbol.hasInstance](value: unknown): value is MeshProtocolFailure {
      return this === MeshProtocolError ? isProtocolFailureInstance(value) : Function.prototype[Symbol.hasInstance].call(this, value);
    }

    constructor(code: string, message: string, status?: number);
    constructor(properties: MeshErrorProperties, options?: Schema.MakeOptions);
    constructor(code: string | MeshErrorProperties, message?: string | Schema.MakeOptions, status = 400) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "", status } : code,
        typeof message === "object" ? message : undefined);
      this.name = "MeshProtocolError";
    }

    toJSON() { return { name: this.name, code: this.code, status: this.status }; }
  }
  return MeshProtocolError;
})();
export type MeshProtocolError = InstanceType<typeof MeshProtocolError>;

export const meshBodyTooLarge = (limit: number) => new MeshProtocolError({
  code: "MESH_BODY_TOO_LARGE", message: `Mesh request body exceeds ${limit} bytes.`, status: 413
});

export const meshTimingUnavailable = () => new MeshProtocolError({
  code: "MESH_REMOTE_TIMING_UNAVAILABLE", message: "Mesh remote timing metadata is unavailable.", status: 400
});

export const meshAuthenticationError = (reason: "invalid" | "stale" | "body") => {
  const failures = {
    invalid: ["MESH_AUTH_INVALID", "Mesh request authentication is invalid."],
    stale: ["MESH_AUTH_STALE", "Mesh request authentication is stale."],
    body: ["MESH_BODY_TAMPERED", "Mesh request content digest does not match."]
  } as const;
  const [code, message] = failures[reason];
  return new MeshProtocolError({ code, message, status: 401 });
};

export const MeshExecutorError = /* @__PURE__ */ (() => {
  class MeshExecutorError extends Schema.TaggedError<MeshExecutorError>()("MeshExecutorError", MeshErrorFields) {
    constructor(code: string, message: string, status?: number);
    constructor(properties: MeshErrorProperties, options?: Schema.MakeOptions);
    constructor(code: string | MeshErrorProperties, message?: string | Schema.MakeOptions, status = 400) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "", status } : code,
        typeof message === "object" ? message : undefined);
      this.name = "MeshExecutorError";
    }

    toJSON() { return { name: this.name, code: this.code, status: this.status }; }
  }
  return MeshExecutorError;
})();
export type MeshExecutorError = InstanceType<typeof MeshExecutorError>;

export const MeshExecutorValidationError = /* @__PURE__ */ (() => {
  class MeshExecutorValidationError extends Schema.TaggedError<MeshExecutorValidationError>()(
    "MeshExecutorValidationError", MeshErrorFields
  ) {
    constructor(message: string, code?: string, status?: number);
    constructor(properties: MeshErrorProperties, options?: Schema.MakeOptions);
    constructor(message: string | MeshErrorProperties, code: string | Schema.MakeOptions = "MESH_PLAN_INVALID", status = 400) {
      super(typeof message === "string" ? { message, code: typeof code === "string" ? code : "MESH_PLAN_INVALID", status } : message,
        typeof code === "object" ? code : undefined);
      this.name = "MeshExecutorValidationError";
    }

    toJSON() { return { name: this.name, code: this.code, status: this.status }; }
  }
  return MeshExecutorValidationError;
})();
export type MeshExecutorValidationError = InstanceType<typeof MeshExecutorValidationError>;

const MeshDockerCommandErrorFields = /* @__PURE__ */ (() => Schema.Struct({ code: Schema.String, message: Schema.String, status: Schema.tag(502) }))();
type MeshDockerCommandErrorProperties = typeof MeshDockerCommandErrorFields["~type.make.in"];
export const MeshDockerCommandError = /* @__PURE__ */ (() => {
  class MeshDockerCommandError extends Schema.TaggedError<MeshDockerCommandError>()("MeshDockerCommandError", MeshDockerCommandErrorFields) {
    constructor(code: string, message: string);
    constructor(properties: MeshDockerCommandErrorProperties, options?: Schema.MakeOptions);
    constructor(code: string | MeshDockerCommandErrorProperties, message?: string | Schema.MakeOptions) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "" } : code,
        typeof message === "object" ? message : undefined);
      this.name = "MeshDockerCommandError";
    }

    toJSON() { return { name: this.name, code: this.code }; }
  }
  return MeshDockerCommandError;
})();
export type MeshDockerCommandError = InstanceType<typeof MeshDockerCommandError>;

const MeshQuotaErrorFields = /* @__PURE__ */ (() => Schema.Struct({ code: Schema.String, message: Schema.String, status: Schema.tag(503) }))();
type MeshQuotaErrorProperties = typeof MeshQuotaErrorFields["~type.make.in"];
export const MeshQuotaError = /* @__PURE__ */ (() => {
  class MeshQuotaError extends Schema.TaggedError<MeshQuotaError>()("MeshQuotaError", MeshQuotaErrorFields) {
    constructor(code: string, message: string);
    constructor(properties: MeshQuotaErrorProperties, options?: Schema.MakeOptions);
    constructor(code: string | MeshQuotaErrorProperties, message?: string | Schema.MakeOptions) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "" } : code,
        typeof message === "object" ? message : undefined);
      this.name = "MeshQuotaError";
    }

    toJSON() { return { name: this.name, code: this.code }; }
  }
  return MeshQuotaError;
})();
export type MeshQuotaError = InstanceType<typeof MeshQuotaError>;

export const MeshWorkloadConnectionError = /* @__PURE__ */ (() => {
  class MeshWorkloadConnectionError extends Schema.TaggedError<MeshWorkloadConnectionError>()("MeshWorkloadConnectionError", {
    message: Schema.tag("Container workload connection failed before request delivery."), status: Schema.tag(409)
  }) {
    constructor(properties = {}, options?: Schema.MakeOptions) {
      super(properties, options);
      this.name = "MeshWorkloadConnectionError";
    }

    toJSON() { return { name: this.name }; }
  }
  return MeshWorkloadConnectionError;
})();
export type MeshWorkloadConnectionError = InstanceType<typeof MeshWorkloadConnectionError>;

export const MeshWorkloadPoolCapacityError = /* @__PURE__ */ (() => {
  class MeshWorkloadPoolCapacityError extends Schema.TaggedError<MeshWorkloadPoolCapacityError>()("MeshWorkloadPoolCapacityError", {
    message: Schema.tag("Container workload connection capacity is exhausted."), status: Schema.tag(502)
  }) {
    constructor(properties = {}, options?: Schema.MakeOptions) {
      super(properties, options);
      this.name = "MeshWorkloadPoolCapacityError";
    }

    toJSON() { return { name: this.name }; }
  }
  return MeshWorkloadPoolCapacityError;
})();
export type MeshWorkloadPoolCapacityError = InstanceType<typeof MeshWorkloadPoolCapacityError>;

const RegistryDeliveryFields = /* @__PURE__ */ (() => Schema.Struct({ ...MeshErrorFields.fields, safeToFallback: Schema.Boolean }))();
type RegistryDeliveryProperties = typeof RegistryDeliveryFields["~type.make.in"];
export const MeshRegistrySessionDeliveryError = /* @__PURE__ */ (() => {
  class MeshRegistrySessionDeliveryError extends Schema.TaggedError<MeshRegistrySessionDeliveryError>()(
    "MeshRegistrySessionDeliveryError", RegistryDeliveryFields
  ) {
    readonly #constructed = true;
    static readonly isConstructed = Schema.is(Schema.declare((value): value is MeshRegistrySessionDeliveryError =>
      typeof value === "object" && value !== null && #constructed in value));

    constructor(code: string, message: string, status: number, safeToFallback: boolean);
    constructor(properties: RegistryDeliveryProperties, options?: Schema.MakeOptions);
    constructor(code: string | RegistryDeliveryProperties, message?: string | Schema.MakeOptions, status = 502, safeToFallback = false) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "", status, safeToFallback } : code,
        typeof message === "object" ? message : undefined);
      this.name = "MeshRegistrySessionDeliveryError";
    }

    toJSON() { return { name: this.name, code: this.code, status: this.status, safeToFallback: this.safeToFallback }; }
  }
  return MeshRegistrySessionDeliveryError;
})();
export type MeshRegistrySessionDeliveryError = InstanceType<typeof MeshRegistrySessionDeliveryError>;

export const MeshStableProxyFallback = /* @__PURE__ */ (() => {
  class MeshStableProxyFallback extends Schema.TaggedError<MeshStableProxyFallback>()(
    "MeshStableProxyFallback", MeshErrorFields
  ) {
    readonly #constructed = true;
    static readonly isConstructed = Schema.is(Schema.declare((value): value is MeshStableProxyFallback =>
      typeof value === "object" && value !== null && #constructed in value));

    constructor(error: MeshErrorProperties, options?: Schema.MakeOptions) {
      super({ code: error.code, message: error.message, status: error.status }, options);
      this.name = "MeshStableProxyFallback";
    }

    toJSON() { return { name: this.name, code: this.code, status: this.status }; }
  }
  return MeshStableProxyFallback;
})();
export type MeshStableProxyFallback = InstanceType<typeof MeshStableProxyFallback>;

// Constructor origin is checked without invoking unknown property/prototype
// access. Legacy instanceof family matching below keeps its existing contract.
export const isConstructedMeshRemoteFailure = (value: unknown): value is MeshRemoteFailure =>
  MeshContainerRemoteError.isConstructed(value) || MeshRegistrySessionDeliveryError.isConstructed(value) || MeshStableProxyFallback.isConstructed(value);

export const MeshSessionOperationError = /* @__PURE__ */ (() => {
  class MeshSessionOperationError extends Schema.TaggedError<MeshSessionOperationError>()(
    "MeshSessionOperationError", MeshErrorFields
  ) {
    readonly #constructed = true;
    static readonly isConstructed = Schema.is(Schema.declare((value): value is MeshSessionOperationError =>
      typeof value === "object" && value !== null && #constructed in value));

    constructor(code: string, message: string, status?: number);
    constructor(properties: MeshErrorProperties, options?: Schema.MakeOptions);
    constructor(code: string | MeshErrorProperties, message?: string | Schema.MakeOptions, status = 400) {
      super(typeof code === "string" ? { code, message: typeof message === "string" ? message : "", status } : code,
        typeof message === "object" ? message : undefined);
      this.name = "MeshProtocolError";
    }

    toJSON() { return { name: this.name, code: this.code, status: this.status }; }
  }
  return MeshSessionOperationError;
})();
export type MeshSessionOperationError = InstanceType<typeof MeshSessionOperationError>;

export const isConstructedMeshProtocolFailure = (value: unknown): value is MeshProtocolFailure =>
  MeshProtocolError.isConstructed(value) || MeshSessionOperationError.isConstructed(value);

// Family membership retains native prototype identity. A plain RPC shape cannot
// grant fallback permission or change a connection failure into a stream failure.
const isRemoteFailureInstance = Schema.is(Schema.Union([
  Schema.declare((value): value is MeshContainerRemoteError =>
    Function.prototype[Symbol.hasInstance].call(MeshContainerRemoteError, value)),
  Schema.instanceOf(MeshRegistrySessionDeliveryError), Schema.instanceOf(MeshStableProxyFallback)
]));
const isProtocolFailureInstance = Schema.is(Schema.Union([
  Schema.declare((value): value is MeshProtocolError =>
    Function.prototype[Symbol.hasInstance].call(MeshProtocolError, value)),
  Schema.instanceOf(MeshSessionOperationError)
]));

export const MeshRemoteFailure = /* @__PURE__ */ (() => Schema.Union([MeshRegistrySessionDeliveryError, MeshStableProxyFallback, MeshContainerRemoteError]))();
export type MeshRemoteFailure = typeof MeshRemoteFailure.Type;
export const MeshProtocolFailure = /* @__PURE__ */ (() => Schema.Union([MeshSessionOperationError, MeshProtocolError]))();
export type MeshProtocolFailure = typeof MeshProtocolFailure.Type;
export const MeshFailure = /* @__PURE__ */ (() => Schema.Union([
  ...MeshRemoteFailure.members, ...MeshProtocolFailure.members, MeshExecutorError, MeshExecutorValidationError,
  MeshDockerCommandError, MeshQuotaError, MeshWorkloadConnectionError, MeshWorkloadPoolCapacityError
]))();
export type MeshFailure = typeof MeshFailure.Type;
